import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useNotifications } from "@/context/NotificationsContext";
import {
  ArrowLeft,
  Menu,
  ShoppingCart,
  X,
  User,
  CalendarDays,
  Trophy,
  Receipt,
} from "lucide-react";
import {
  getNotificationHref,
  getTournamentNotificationCta,
  isUnreadTournamentPaymentNotification,
} from "@/lib/notificationRoutes";
import useMyTournamentRegistrations from "@/hooks/useMyTournamentRegistrations";
import {
  formatRegistrationFee,
  tournamentRegistrationStatusMeta,
} from "@/lib/tournamentRegistrations";
import { emitLandingNav } from "@/constants/tournaments";

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

const VISITCAMSUR_URL = "https://book.visitcamsur.com/";
const NAVBAR_LOGO_SRC = "/newassets/logo%201.png";

/** Homepage section anchors + page routes mixed into one nav list */
const NAV_ITEMS = [
  { type: "section", label: "Home", id: "home" },
  { type: "section", label: "Tournaments", id: "tournament-section" },
  { type: "section", label: "What's New", id: "whats-new" },
  { type: "section", label: "Gallery", id: "gallery" },
  { type: "section", label: "Facility", id: "facility" },
  { type: "section", label: "Rates", id: "rates" },
  { type: "section", label: "Rent", id: "rent" },
  { type: "section", label: "Court Rules", id: "court-rules" },
  { type: "section", label: "Location", id: "location" },
];

/** Section ids whose background is light → navbar text should be dark for contrast */
const LIGHT_SECTION_IDS = new Set([
  "tournament",
  "gallery",
  "facility",
  "rates",
  "rent",
  "location",
]);

/** True when the fixed navbar sits over a light surface (dark nav text). */
const isNavbarOverLightSection = (sampleY) => {
  for (const item of NAV_ITEMS) {
    if (item.type !== "section") continue;
    const section = document.getElementById(item.id);
    if (!section) continue;
    const rect = section.getBoundingClientRect();
    if (rect.top <= sampleY && rect.bottom > sampleY) {
      // Tournament block: dark hero banner vs light registration panel
      if (item.id === "tournament-section") {
        const regPanel = document.getElementById("tournament");
        if (regPanel) {
          const regRect = regPanel.getBoundingClientRect();
          if (regRect.top <= sampleY && regRect.bottom > sampleY) {
            return true;
          }
        }
        // upcoming-tournaments banner (dark image) — keep white navbar text
        return false;
      }
      return LIGHT_SECTION_IDS.has(item.id);
    }
  }
  return false;
};

const isIncompleteCheckout = (r) => {
  if (!r || r.payment_verified) return false;
  const st = String(r.status || "").toLowerCase();
  return ["pending_payment", "payment_failed", "expired"].includes(st);
};

const registrationEventsHref = (reg) => {
  const qs = new URLSearchParams({ tab: "registered" });
  if (reg?.tournament_id) qs.set("highlight", reg.tournament_id);
  if (reg?.participant_id) qs.set("participant_id", reg.participant_id);
  return `/events?${qs.toString()}`;
};

/** Compact nav link. When scrolled, a centered lime underline only — fill lives on the bar. */
const desktopNavClass = (overLight, scrolled) => {
  const base =
    "relative whitespace-nowrap rounded-md px-2 py-1.5 text-[13px] font-bold uppercase tracking-tight transition-all duration-200 hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#c7e150] xl:px-2.5 xl:text-sm";
  const hover = overLight ? "hover:bg-black/10" : "hover:bg-white/20";
  const underline = scrolled
    ? "after:absolute after:bottom-0.5 after:left-1/2 after:h-[3px] after:w-[70%] after:-translate-x-1/2 after:rounded-full after:bg-[#c7e150] after:content-[''] hover:after:w-[85%]"
    : "";
  return `${base} ${hover} ${underline}`;
};

/** True after the landing hero (#home) has scrolled past the fixed navbar. */
const isNavbarPastHero = (navHeight) => {
  const home = document.getElementById("home");
  if (home) {
    return home.getBoundingClientRect().bottom <= navHeight + 8;
  }
  return window.scrollY > 80;
};

const actionBtnClass =
  "inline-flex min-h-[40px] items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-[13px] font-bold uppercase tracking-tight transition-all duration-200 hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white xl:px-4 xl:text-sm";

/**
 * @param {{ forceDarkText?: boolean }} props
 * forceDarkText — use on light pages (e.g. /tournaments) so nav stays #10233f
 */
export default function PublicNavbar({ forceDarkText = false }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [detectedLight, setDetectedLight] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [mobileEventsOpen, setMobileEventsOpen] = useState(false);
  /** Snapshot so mark-as-read on open doesn't empty the panel mid-view. */
  const [eventsPanelItems, setEventsPanelItems] = useState([]);
  const { user, logout, token } = useAuth();
  const { getItemCount, openCart } = useCart();
  const navigate = useNavigate();
  const { notifications, markAsRead } = useNotifications();
  const {
    registrations,
    joinedRegistrations,
    refresh: refreshMyRegistrations,
  } = useMyTournamentRegistrations();

  const unreadPaidEvents = useMemo(
    () => notifications.filter(isUnreadTournamentPaymentNotification),
    [notifications],
  );
  const unreadPaidCount = unreadPaidEvents.length;
  const incompleteCheckouts = useMemo(
    () => (Array.isArray(registrations) ? registrations : []).filter(isIncompleteCheckout),
    [registrations],
  );
  const panelRegistrations = useMemo(
    () => [...joinedRegistrations, ...incompleteCheckouts],
    [joinedRegistrations, incompleteCheckouts],
  );

  const openEventsPanel = useCallback(async (items) => {
    const snapshot = Array.isArray(items) ? [...items] : [];
    setEventsPanelItems(snapshot);
    if (token) {
      await refreshMyRegistrations({ silent: true });
    }
    await Promise.all(
      snapshot
        .filter((n) => n?.notification_id && !n.read)
        .map((n) => markAsRead(n.notification_id)),
    );
  }, [markAsRead, refreshMyRegistrations, token]);

  const handleEventsOpenChange = useCallback((open) => {
    setEventsOpen(open);
    if (open) {
      void openEventsPanel(unreadPaidEvents);
    } else {
      setEventsPanelItems([]);
    }
  }, [openEventsPanel, unreadPaidEvents]);

  const closeEventsMenus = useCallback(() => {
    setEventsOpen(false);
    setMobileEventsOpen(false);
    setMenuOpen(false);
    setEventsPanelItems([]);
  }, []);

  const openPaidEventReceipt = useCallback(async (notification) => {
    if (!notification?.read && notification?.notification_id) {
      await markAsRead(notification.notification_id);
    }
    closeEventsMenus();
    navigate(getNotificationHref(notification));
  }, [markAsRead, navigate, closeEventsMenus]);

  const openRegistrationRow = useCallback((reg) => {
    closeEventsMenus();
    navigate(registrationEventsHref(reg));
  }, [closeEventsMenus, navigate]);

  const cartCount = getItemCount();
  const overLight = forceDarkText || detectedLight;

  // Flip navbar text to dark when the section sitting under the bar has a light background.
  // The opaque bar only appears after #home (hero) has scrolled away.
  useEffect(() => {
    let frame = null;

    const evaluate = () => {
      frame = null;
      const navEl = document.getElementById("public-navbar");
      const navHeight = navEl?.offsetHeight ?? 64;
      if (forceDarkText) {
        setDetectedLight(true);
        setScrolled(window.scrollY > 80);
        return;
      }
      setScrolled(isNavbarPastHero(navHeight));
      const sampleY = navHeight + 8;
      setDetectedLight(isNavbarOverLightSection(sampleY));
    };

    const onScroll = () => {
      if (frame === null) frame = window.requestAnimationFrame(evaluate);
    };

    evaluate();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [forceDarkText]);

  const scrollToSection = useCallback((sectionId) => {
    emitLandingNav();
    const tryScroll = () => {
      const element = document.getElementById(sectionId);
      if (!element) return false;
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      return true;
    };

    if (tryScroll()) {
      setMenuOpen(false);
      return;
    }

    // Off the homepage, or section still lazy-loading — go home then retry
    navigate(`/#${sectionId}`);
    setMenuOpen(false);
    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (tryScroll() || attempts > 40) window.clearInterval(timer);
    }, 50);
  }, [navigate]);

  const handleNavItem = useCallback((item) => {
    emitLandingNav();
    if (item.type === "route") {
      navigate(item.to);
      setMenuOpen(false);
      return;
    }
    scrollToSection(item.id);
  }, [navigate, scrollToSection]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  const panelSubtitle = eventsPanelItems.length > 0
    ? `${eventsPanelItems.length} new update${eventsPanelItems.length === 1 ? "" : "s"}`
    : panelRegistrations.length > 0
      ? `${panelRegistrations.length} registration${panelRegistrations.length === 1 ? "" : "s"}`
      : "Your tournament registrations";

  const renderAlertRows = (variant) => eventsPanelItems.map((n) => {
    const cta = getTournamentNotificationCta(n);
    const incomplete = String(n.type || "").toLowerCase() === "tournament_payment_incomplete";
    const mobile = variant === "mobile";
    return (
      <button
        key={n.notification_id}
        type="button"
        className={mobile
          ? "w-full rounded-lg bg-white/10 px-3 py-2.5 text-left text-white transition hover:bg-white/15"
          : "flex w-full items-start gap-2.5 border-b px-3 py-2.5 text-left last:border-b-0 hover:bg-muted/70"}
        onClick={() => openPaidEventReceipt(n)}
      >
        {!mobile && (
          <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
            incomplete ? "bg-amber-100 text-amber-700" : "bg-[#1e4b85]/10 text-[#1e4b85]"
          }`}>
            <Trophy className="h-4 w-4" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className={`truncate text-sm font-semibold ${mobile ? "text-white" : ""}`}>{n.title}</p>
          <p className={`mt-0.5 line-clamp-2 text-xs ${mobile ? "text-white/75" : "text-muted-foreground"}`}>
            {n.message}
          </p>
          {cta && (
            <p className={`mt-1 text-xs font-semibold ${
              mobile ? "text-[#c7e150]" : (incomplete ? "text-amber-700" : "text-emerald-700")
            }`}>
              {cta}
            </p>
          )}
        </div>
      </button>
    );
  });

  const renderRegistrationRows = (variant) => panelRegistrations.map((reg) => {
    const incomplete = isIncompleteCheckout(reg);
    const meta = incomplete
      ? {
        label: reg.registration_status || "Awaiting payment",
        badgeClass: "bg-amber-100 text-amber-800",
      }
      : tournamentRegistrationStatusMeta(reg);
    const fee = formatRegistrationFee(reg.registration_fee || reg.entry_fee);
    const mobile = variant === "mobile";
    return (
      <button
        key={reg.participant_id || `${reg.tournament_id}-reg`}
        type="button"
        className={mobile
          ? "w-full rounded-lg bg-white/10 px-3 py-2.5 text-left text-white transition hover:bg-white/15"
          : "flex w-full items-start gap-2.5 border-b px-3 py-2.5 text-left last:border-b-0 hover:bg-muted/70"}
        onClick={() => openRegistrationRow(reg)}
      >
        {!mobile && (
          <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
            incomplete ? "bg-amber-100 text-amber-700" : "bg-[#1e4b85]/10 text-[#1e4b85]"
          }`}>
            <Trophy className="h-4 w-4" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className={`truncate text-sm font-semibold ${mobile ? "text-white" : ""}`}>
            {reg.title || reg.category_label || "Tournament"}
          </p>
          <p className={`mt-0.5 truncate text-xs ${mobile ? "text-white/75" : "text-muted-foreground"}`}>
            {[reg.category_label, fee].filter(Boolean).join(" · ")}
          </p>
          <p className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${
            mobile ? "bg-white/15 text-[#c7e150]" : meta.badgeClass
          }`}>
            {meta.label}
          </p>
        </div>
      </button>
    );
  });

  return (
    <>
      <nav
        id="public-navbar"
        className={`fixed left-0 right-0 top-0 z-50 transition-colors duration-300 ${
          overLight
            ? "text-[#10233f]"
            : "text-white [text-shadow:0_1px_4px_rgba(0,0,0,0.55)]"
        }`}
        aria-label="Main navigation"
      >
        <div className="w-full">
          <div className="mx-auto flex min-h-[64px] max-w-screen-2xl items-center gap-2 px-3 sm:px-4 lg:px-5">
            <Link
              to="/"
              className="flex shrink-0 items-center"
              aria-label="CamSur Pickleball Club home"
              onClick={() => setMenuOpen(false)}
            >
              <img
                src={NAVBAR_LOGO_SRC}
                alt="CamSur Pickleball Club"
                width={120}
                height={140}
                decoding="async"
                loading="eager"
                className="mt-2 h-16 w-auto max-w-none object-contain object-center sm:h-20 [image-rendering:auto] [-webkit-backface-visibility:hidden] [backface-visibility:hidden] [transform:translateZ(0)]"
                style={{
                  filter: "drop-shadow(0 0 0.75px rgba(255,255,255,0.12))",
                }}
              />
            </Link>

            <div
              className={`flex min-w-0 flex-1 items-center gap-2 rounded-2xl px-2 py-1.5 transition-colors duration-300 ${
                scrolled
                  ? overLight
                    ? "bg-white/95 shadow-sm"
                    : "bg-[#10233f]/95 shadow-sm"
                  : ""
              }`}
            >
            {/* Nav links — centered, single line (lg+) */}
            <div className="hidden min-w-0 flex-1 items-center justify-center lg:flex">
              <div className="flex flex-nowrap items-center gap-0.5 xl:gap-1">
                {NAV_ITEMS.map((item) => (
                  <button
                    key={item.type === "route" ? item.to : item.id}
                    type="button"
                    onClick={() => handleNavItem(item)}
                    className={desktopNavClass(overLight, scrolled)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="ml-auto flex shrink-0 items-center gap-1.5 lg:ml-0">
              <a
                href={VISITCAMSUR_URL}
                aria-label="Back to VisitCamSur"
                className={`group hidden items-center gap-2 overflow-hidden rounded-full px-4 py-2 text-[13px] font-bold uppercase tracking-widest backdrop-blur-sm transition-all duration-300 active:scale-95 sm:inline-flex xl:px-5 xl:text-sm ${
                  overLight
                    ? "bg-black/10 text-[#10233f] hover:bg-black/20 hover:shadow-[0_0_16px_rgba(0,0,0,0.15)]"
                    : "bg-white/15 text-white hover:bg-white/25 hover:shadow-[0_0_16px_rgba(199,225,80,0.35)]"
                }`}
              >
                <ArrowLeft className="h-3.5 w-3.5 shrink-0 transition-transform duration-300 group-hover:-translate-x-0.5" />
                <span className="hidden xl:inline">VisitCamSur</span>
                <span className="xl:hidden">CamSur</span>
              </a>

              <Link
                to="/book/venue_camsur"
                className={`${actionBtnClass} hidden bg-[#f48a15] text-white hover:bg-[#e07d0f] md:inline-flex`}
              >
                <CalendarDays className="h-4 w-4 shrink-0" />
                Book
              </Link>

              {user && (
                <DropdownMenu
                  open={eventsOpen}
                  onOpenChange={handleEventsOpenChange}
                >
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className={`${actionBtnClass} relative hidden bg-[#1e4b85] text-white hover:bg-[#163a68] md:inline-flex`}
                      aria-label={
                        unreadPaidCount > 0
                          ? `Events — ${unreadPaidCount} new paid tournament receipt${unreadPaidCount === 1 ? "" : "s"}`
                          : "Events"
                      }
                    >
                      <Trophy className="h-4 w-4 shrink-0" />
                      Events
                      {unreadPaidCount > 0 && (
                        <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white shadow-sm">
                          {unreadPaidCount > 99 ? "99+" : unreadPaidCount}
                        </span>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-[320px] p-0">
                    <div className="border-b px-3 py-2.5">
                      <p className="text-sm font-semibold text-[#1e4b85]">Tournament updates</p>
                      <p className="text-[11px] text-muted-foreground">{panelSubtitle}</p>
                    </div>
                    <div className="max-h-[320px] overflow-y-auto">
                      {eventsPanelItems.length === 0 && panelRegistrations.length === 0 ? (
                        <div className="px-3 py-6 text-center">
                          <Receipt className="mx-auto mb-2 h-7 w-7 text-muted-foreground" />
                          <p className="text-sm font-medium">No tournament registrations yet</p>
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            Register for an event to see it here
                          </p>
                        </div>
                      ) : (
                        <>
                          {eventsPanelItems.length > 0 && (
                            <div>
                              <p className="px-3 pt-2 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                                New alerts
                              </p>
                              {renderAlertRows("desktop")}
                            </div>
                          )}
                          {panelRegistrations.length > 0 && (
                            <div>
                              <p className="px-3 pt-2 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                                My registrations
                              </p>
                              {renderRegistrationRows("desktop")}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <div className="border-t p-2">
                      <DropdownMenuItem
                        className="cursor-pointer justify-center font-semibold text-[#1e4b85]"
                        onClick={() => {
                          closeEventsMenus();
                          navigate("/events");
                        }}
                      >
                        My Events
                      </DropdownMenuItem>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {user && (
                <button
                  type="button"
                  onClick={openCart}
                  className={`relative flex min-h-[40px] min-w-[40px] items-center justify-center rounded-full transition hover:scale-105 ${overLight ? "hover:bg-black/10" : "hover:bg-white/15"}`}
                  aria-label="Open cart"
                >
                  <ShoppingCart className="h-6 w-6" />
                  {cartCount > 0 && (
                    <span className="absolute right-0 top-0 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold text-white shadow-sm">
                      {cartCount > 99 ? "99+" : cartCount}
                    </span>
                  )}
                </button>
              )}

              {!user ? (
                <Link
                  to="/login"
                  className={`${actionBtnClass} hidden border-2 lg:inline-flex ${overLight ? "border-[#10233f]/40 hover:bg-black/5" : "border-white/30 hover:bg-white/10"}`}
                >
                  <User className="h-4 w-4" />
                  Log in
                </Link>
              ) : (
                <>
                  {/* Mobile: always-visible account avatar → profile */}
                  <button
                    type="button"
                    onClick={() => {
                      navigate("/profile");
                      setMenuOpen(false);
                    }}
                    className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full ring-2 transition hover:scale-105 lg:hidden ${
                      overLight
                        ? "ring-[#10233f]/25 hover:ring-[#10233f]/45"
                        : "ring-white/50 hover:ring-white/80"
                    }`}
                    aria-label={`Open profile${user?.name ? ` for ${user.name}` : ""}`}
                  >
                    <Avatar className="h-9 w-9 bg-[#f48a15]">
                      <AvatarImage src={user?.picture} alt="" />
                      <AvatarFallback className="bg-[#f48a15] text-sm font-bold text-white">
                        {user?.name?.charAt(0)?.toUpperCase() || "U"}
                      </AvatarFallback>
                    </Avatar>
                  </button>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className={`hidden h-10 min-h-[40px] gap-2 rounded-full px-2 lg:inline-flex ${
                          overLight
                            ? "text-[#10233f] hover:bg-black/10 hover:text-[#10233f]"
                            : "text-white hover:bg-white/10 hover:text-white [text-shadow:0_1px_4px_rgba(0,0,0,0.55)]"
                        }`}
                      >
                        <Avatar className="h-8 w-8 bg-[#f48a15]">
                          <AvatarImage src={user?.picture} />
                          <AvatarFallback className="bg-[#f48a15] text-sm font-bold text-white">
                            {user?.name?.charAt(0)?.toUpperCase() || "U"}
                          </AvatarFallback>
                        </Avatar>
                        <span
                          className={`max-w-[100px] truncate text-sm font-bold ${
                            overLight ? "text-[#10233f]" : "text-white"
                          }`}
                        >
                          {user?.name?.split(" ")[0]}
                        </span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52 text-base">
                      <DropdownMenuItem
                        className="py-3 text-base font-medium"
                        onClick={() => navigate("/profile")}
                      >
                        Profile
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="py-3 text-base font-medium"
                        onClick={() => navigate("/my-bookings")}
                      >
                        My Bookings
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="py-3 text-base font-medium text-destructive"
                        onClick={logout}
                      >
                        Log out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}

              {/* Hamburger — shown below lg; always show Menu / Close label */}
              <button
                type="button"
                className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-1 rounded-xl border-2 px-2.5 transition-all duration-200 hover:scale-105 hover:border-[#c7e150] sm:min-h-[48px] sm:gap-1.5 sm:px-3 lg:hidden ${overLight ? "border-[#10233f]/30 bg-black/5 hover:bg-black/10" : "border-white/25 bg-white/10 hover:bg-white/20"}`}
                aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
                aria-expanded={menuOpen}
                aria-controls="mobile-nav-panel"
                onClick={() => setMenuOpen((open) => {
                  if (!open) emitLandingNav();
                  return !open;
                })}
              >
                {menuOpen ? (
                  <>
                    <X className="h-6 w-6 shrink-0 sm:h-7 sm:w-7" aria-hidden="true" />
                    <span className="text-sm font-bold uppercase">Close</span>
                  </>
                ) : (
                  <>
                    <Menu className="h-6 w-6 shrink-0 sm:h-7 sm:w-7" aria-hidden="true" />
                    <span className="text-sm font-bold uppercase">Menu</span>
                  </>
                )}
              </button>
            </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile hamburger drawer */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
          />

          <div
            id="mobile-nav-panel"
            className="absolute left-0 right-0 top-[4.75rem] max-h-[calc(100dvh-4.75rem)] overflow-y-auto border-b-4 border-[#c7e150] bg-[#1e4b85] shadow-2xl sm:top-20 sm:max-h-[calc(100dvh-5rem)]"
          >
            <div className="mx-auto max-w-lg px-4 py-5 sm:px-6">
              {/* Account — first so logged-in users can open Profile immediately */}
              {user && (
                <button
                  type="button"
                  onClick={() => {
                    navigate("/profile");
                    setMenuOpen(false);
                  }}
                  className="mb-5 flex w-full items-center gap-3 rounded-2xl border-2 border-[#c7e150]/60 bg-[#163a66] px-3 py-3 text-left transition hover:bg-[#10233f] active:scale-[0.99]"
                  aria-label="View your profile"
                >
                  <Avatar className="h-14 w-14 shrink-0 bg-[#f48a15] ring-2 ring-[#c7e150]/80">
                    <AvatarImage src={user?.picture} alt="" />
                    <AvatarFallback className="bg-[#f48a15] text-lg font-bold text-white">
                      {user?.name?.charAt(0)?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-base font-bold text-white">
                      {user?.name || "Your account"}
                    </p>
                    <p className="truncate text-xs text-white/70">
                      {user?.email || "Tap to open profile"}
                    </p>
                    <p className="mt-1 text-xs font-bold uppercase tracking-wide text-[#c7e150]">
                      View profile →
                    </p>
                  </div>
                </button>
              )}

              <p className="mb-4 text-center text-base font-bold uppercase tracking-wide text-[#c7e150]">
                Navigate
              </p>

              {/* Primary actions — always visible in mobile menu */}
              <div className="mb-5 flex flex-col gap-3">
                <a
                  href={VISITCAMSUR_URL}
                  className="flex min-h-[52px] items-center justify-center gap-3 rounded-xl bg-[#c7e150] px-4 text-base font-bold uppercase tracking-wide text-[#1e4b85] shadow-[3px_3px_0px_#163a66] transition hover:scale-[1.02] active:scale-[0.98]"
                >
                  <ArrowLeft className="h-5 w-5" />
                  Back to VisitCamSur
                </a>
                <Link
                  to="/book/venue_camsur"
                  onClick={() => setMenuOpen(false)}
                  className="flex min-h-[52px] items-center justify-center gap-3 rounded-xl bg-[#f48a15] px-4 text-base font-bold uppercase tracking-wide text-white transition hover:scale-[1.02] active:scale-[0.98]"
                >
                  <CalendarDays className="h-5 w-5" />
                  Book a court
                </Link>
                {user && (
                  <div className="overflow-hidden rounded-xl bg-[#163a66]">
                    <button
                      type="button"
                      onClick={async () => {
                        const next = !mobileEventsOpen;
                        setMobileEventsOpen(next);
                        if (next) {
                          await openEventsPanel(unreadPaidEvents);
                        } else {
                          setEventsPanelItems([]);
                        }
                      }}
                      className="relative flex min-h-[52px] w-full items-center justify-center gap-3 px-4 text-base font-bold uppercase tracking-wide text-white transition hover:bg-white/5"
                      aria-expanded={mobileEventsOpen}
                      aria-label={
                        unreadPaidCount > 0
                          ? `Events — ${unreadPaidCount} new paid tournament receipt${unreadPaidCount === 1 ? "" : "s"}`
                          : "Events"
                      }
                    >
                      <Trophy className="h-5 w-5" />
                      Events
                      {unreadPaidCount > 0 && (
                        <span className="flex h-6 min-w-[24px] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-bold text-white">
                          {unreadPaidCount > 99 ? "99+" : unreadPaidCount}
                        </span>
                      )}
                    </button>
                    {mobileEventsOpen && (
                      <div className="border-t border-white/15 bg-[#10233f] px-3 py-3">
                        {eventsPanelItems.length === 0 && panelRegistrations.length === 0 ? (
                          <p className="mb-3 text-center text-sm text-white/80">
                            No tournament registrations yet. Open My Events to register.
                          </p>
                        ) : (
                          <div className="mb-3 flex flex-col gap-2">
                            {eventsPanelItems.length > 0 && (
                              <>
                                <p className="text-[10px] font-bold uppercase tracking-wide text-white/60">
                                  New alerts
                                </p>
                                {renderAlertRows("mobile")}
                              </>
                            )}
                            {panelRegistrations.length > 0 && (
                              <>
                                <p className="text-[10px] font-bold uppercase tracking-wide text-white/60">
                                  My registrations
                                </p>
                                {renderRegistrationRows("mobile")}
                              </>
                            )}
                          </div>
                        )}
                        <Link
                          to="/events"
                          onClick={() => setMenuOpen(false)}
                          className="flex min-h-[44px] items-center justify-center rounded-lg bg-[#c7e150] px-3 text-sm font-bold uppercase tracking-wide text-[#1e4b85]"
                        >
                          My Events
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Section links — large tap targets */}
              <ul className="flex flex-col gap-2">
                {NAV_ITEMS.map((item) => (
                  <li key={item.type === "route" ? item.to : item.id}>
                    <button
                      type="button"
                      onClick={() => handleNavItem(item)}
                      className="flex min-h-[52px] w-full items-center rounded-xl border-2 border-white/15 bg-white/5 px-5 text-left text-base font-bold uppercase tracking-wide text-white transition-all duration-200 hover:scale-[1.01] hover:border-[#c7e150] hover:bg-white/15 hover:text-lg active:scale-[0.99]"
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>

              {/* Auth */}
              <div className="mt-6 border-t-2 border-white/15 pt-5">
                {!user ? (
                  <Link
                    to="/login"
                    onClick={() => setMenuOpen(false)}
                    className="flex min-h-[52px] w-full items-center justify-center gap-3 rounded-xl border-2 border-white/30 text-base font-bold uppercase tracking-wide text-white transition hover:bg-white/10 hover:text-lg"
                  >
                    <User className="h-5 w-5" />
                    Log in / Sign up
                  </Link>
                ) : (
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        openCart();
                        setMenuOpen(false);
                      }}
                      className="flex min-h-[52px] items-center gap-3 rounded-xl px-5 text-base font-bold text-white transition hover:bg-white/10"
                    >
                      <ShoppingCart className="h-5 w-5" />
                      Cart{cartCount > 0 ? ` (${cartCount})` : ""}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        navigate("/profile");
                        setMenuOpen(false);
                      }}
                      className="flex min-h-[52px] items-center gap-3 rounded-xl border-2 border-white/25 bg-white/10 px-5 text-left text-base font-bold uppercase tracking-wide text-white hover:border-[#c7e150] hover:bg-white/15"
                    >
                      <User className="h-5 w-5 shrink-0" />
                      My Profile
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        navigate("/my-bookings");
                        setMenuOpen(false);
                      }}
                      className="flex min-h-[48px] items-center rounded-xl px-5 text-left text-base font-semibold text-white hover:bg-white/10"
                    >
                      My Bookings
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        logout();
                        setMenuOpen(false);
                      }}
                      className="flex min-h-[48px] items-center rounded-xl px-5 text-left text-base font-semibold text-red-300 hover:bg-white/5"
                    >
                      Log out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
